import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { ITEMS_BASE_API_URL } from "src/utils/constants";
import { IMPERSONATION_HEADER, IMPERSONATION_STORAGE_KEY } from "./constants";

// DM endpoints now live on the Items API (folded in via the API-side
// rewrite). ITEMS_BASE_API_URL is "/rpitems-swa/V1" in local dev (the Vite
// proxy strips that prefix and forwards to localhost:7207) and the full
// APIM gateway URL — e.g. https://ge-dev-api.../merch/rp/items-swa/V1 —
// in deployed environments. Pointing baseURL at it means /dm/* URLs the
// hooks emit get routed through the same gateway the rest of Items uses.
export const apiClient: AxiosInstance = axios.create({
  baseURL: ITEMS_BASE_API_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Module-level token cache. The bridge component in dm/utils/DmTokenBridge.tsx
// pushes RP's MSAL access token here whenever it changes (or the local-dev
// stub when VITE_LOCAL_MODE=true). The request interceptor below picks it up
// and attaches it as a Bearer header. Kept outside React state because axios
// interceptors run outside the React tree.
let currentApiToken: string | null = null;
export const setDmApiToken = (token: string | null) => {
  currentApiToken = token;
};

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Rewrite the SPA-internal `/dm/V1/...` URL convention to the controller
  // routes the merged Items API actually exposes (`/api/dealmanagement/...`).
  // Folding DM into Items dropped the proxy layer that used to do this in
  // dev, and Static Web Apps doesn't proxy by default in deployed envs —
  // doing the rewrite here keeps every hook's URL constant readable and
  // works identically in dev + dev/prod APIM.
  if (typeof config.url === "string" && config.url.startsWith("/dm/V1/")) {
    config.url = config.url.replace(/^\/dm\/V1\//, "/api/dealmanagement/");
  }

  // Attach bearer when we have a real token. The local-dev stub is filtered
  // out — the DM API's BannerFilterMiddleware treats "no Authorization" as a
  // dev request and grants all banners, so we deliberately omit the header
  // in that case rather than send a bad token.
  if (currentApiToken && currentApiToken !== "local-dev-bypass-token") {
    config.headers.Authorization = `Bearer ${currentApiToken}`;
  }

  // Dev impersonation: ImpersonationSwitcher writes the selected mock user
  // id to localStorage; the DM API's UserContextService consumes it via the
  // X-Impersonate-User header in Development mode and rebuilds the request
  // principal off the canned MockWebSec2Provider data.
  if (import.meta.env.MODE === "development") {
    try {
      const impersonate = window.localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (impersonate && impersonate.trim().length > 0) {
        config.headers.set(IMPERSONATION_HEADER, impersonate.trim());
      }
    } catch {
      // SSR or storage-disabled — ignore.
    }
  }
  return config;
});
